import type { Cadeia } from '../tipos';
import { jogadasDe } from './trama';

/**
 * Regras do modo Nitro (§22.2).
 *
 * O contrário do Átrio: lá o número de jogadas é fixo e o placar é o quanto se
 * tira delas; aqui o tempo é fixo e o placar é quantas jogadas cabem dentro
 * dele. Uma partida cobra otimização, a outra cobra velocidade.
 *
 * O relógio **não** mora aqui. O ADR-0001 proíbe o núcleo de conhecer o tempo,
 * então quem conta é a camada React: ela injeta o instante do fim ao começar
 * (`iniciar-corrida`) e avisa quando ele chega (`tempo-esgotado`). O que o
 * núcleo guarda é um número — do mesmo jeito que `salvoEm` na persistência.
 *
 * O prazo é um instante absoluto, e não um saldo que se decrementa. Isso é o
 * que faz o tempo correr no relógio de parede: fechar a aba não congela a
 * partida, e recarregar não devolve segundos (T12-05).
 */

/** Durações oferecidas, em segundos (§22.2). */
export const DURACOES = [30, 60, 120] as const;

export type Duracao = (typeof DURACOES)[number];

export const DURACAO_PADRAO: Duracao = 60;

export function ehDuracao(valor: number): valor is Duracao {
  return (DURACOES as readonly number[]).includes(valor);
}

/** Rótulo curto de uma duração: `30s`, `1min`, `2min`. */
export function rotularDuracao(segundos: number): string {
  if (segundos < 60) return `${segundos}s`;
  const minutos = segundos / 60;
  return `${minutos}min`;
}

/**
 * Placar do Nitro: quantas palavras o jogador escreveu.
 *
 * Cada jogada confirmada põe duas palavras no tabuleiro, mas só uma é dele —
 * a vertical veio do sistema. Contar as duas inflaria o placar com o que o
 * jogador não fez.
 */
export function palavrasEscritas(cadeia: Cadeia): number {
  return jogadasDe(cadeia);
}

/** Milissegundos que faltam. Quem tem o relógio é quem chama (ADR-0001). */
export function restanteMs(terminaEm: number, agora: number): number {
  return Math.max(0, terminaEm - agora);
}

export function tempoAcabou(terminaEm: number, agora: number): boolean {
  return agora >= terminaEm;
}

/** `0:07`, `1:30` — minutos e segundos, sempre com dois dígitos no segundo. */
export function formatarTempo(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const minutos = Math.floor(total / 60);
  const segundos = total % 60;
  return `${minutos}:${String(segundos).padStart(2, '0')}`;
}
